import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectGreenhouse } from 'state';
import { usageRequests } from 'api';
import {
  Container,
  Filters,
  DateInput,
  HelpText,
  AccentText,
  SelectOption,
  InfoContainer,
  Info
} from './UsageStatistic.styles';
import {
  StyledTable,
  TableHead
} from '../GreenhouseManagement/GreenhouseManagement.styles';
import { round } from 'lodash-es';
import { Select } from '../_shared/Select';
import { getISOMonthDateRange } from '../CanopyDetection/helpers';
import GaugeChart from '../Gauge/Gauge';
import { ChartsContainer } from '../CanopyDetection/CanopyDetection.styles';
import { Pagination } from '../_shared/Pagination';

export const UsageStatistic = () => {
  const [monthly, setMonthly] = useState(null);
  const [usageStatistic, setUsageStatistic] = useState({
    jobs_data: undefined,
    total_scanned_area: undefined
  });
  const [dataCount, setDataCount] = useState();
  const [usageChartData, setUsageChartData] = useState([]);
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 2);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };
  const { startDate, endDate } = getCurrentMonthRange();
  const [filterState, setFilterState] = useState({
    day_from: startDate,
    day_to: endDate
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const { startDate, endDate } = getCurrentMonthRange();
    getUsageData(startDate, endDate, 1);
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const current_year = new Date().getFullYear();
  const monthSelectItems = useMemo(() => {
    function mapCB(_, i) {
      const { start, end } = getISOMonthDateRange(i + 1, current_year);
      const monthName = new Date(current_year, i).toLocaleString('default', {
        month: 'long'
      });
      const option = `${monthName} (${start} - ${end})`;
      return {
        name: <SelectOption>{option}</SelectOption>,
        value: i + 1
      };
    }
    return [...Array(12)].map(mapCB);
  }, [current_year]);
  const getUsageData = (startDate, endDate, page) => {
    setUsageStatistic({});
    setLoading(true);
    usageRequests
      .getUsageData(startDate, endDate, page)
      .then(({ data }) => {
        setUsageStatistic(data.results);
        setDataCount(data.count);
        setCurrentPage(page);
      })
      .finally(() => setLoading(false));
  };
  const getChartData = (startDate, endDate) => {
    setUsageChartData([]);
    setLoading(true);
    usageRequests
      .getChartData(startDate, endDate)
      .then(({ data }) => {
        setUsageChartData(data);
      })
      .finally(() => setLoading(false));
  };
  const handleFilterStateChange = ({ filter }) => {
    const { day_from, day_to } = filter || {};
    if (!day_from || !day_to) return;
    const from = new Date(day_from);
    const to = new Date(day_to);
    if (from <= to) {
      getUsageData(day_from, day_to, 1);
    }
  };

  const handleDateFromChange = ({ target }) => {
    const updatedFilter = {
      ...filterState,
      day_from: target.value
    };
    setFilterState(updatedFilter);
    handleFilterStateChange({ filter: updatedFilter });
  };
  const handleDateToChange = ({ target }) => {
    const updatedFilter = {
      ...filterState,
      day_to: target.value
    };
    setFilterState(updatedFilter);
    handleFilterStateChange({ filter: updatedFilter });
  };
  const handleMonthChange = ({ value }) => {
    const { start, end } = getISOMonthDateRange(value, current_year);
    setFilterState({ day_to: end, day_from: start });
    getUsageData(start, end, 1);
    getChartData(start, end);
  };

  return (
    <Container>
      <Filters>
        <Select
          items={[
            { name: 'By Date', value: 'By Date' },
            { name: 'Monthly', value: 'Monthly' }
          ]}
          onSelect={({ value }) => {
            setMonthly(value === 'Monthly');
            setUsageStatistic({
              jobs_data: undefined,
              total_scanned_area: undefined
            });
            setUsageChartData([]);
            setFilterState({});
          }}
          value={monthly !== null && (monthly ? 'Monthly' : 'By Date')}
          placeholder='Select type of view'
          label='Type of view'
        />
        {monthly !== null && (
          <>
            {monthly ? (
              <Select
                items={monthSelectItems}
                onSelect={handleMonthChange}
                value={filterState.month}
                placeholder='Select month'
                label='Month'
              />
            ) : (
              <>
                <DateInput
                  id='input-1'
                  type='date'
                  label='Day From'
                  fullWidth
                  placeholder='Select Day From'
                  value={filterState.day_from ?? ''}
                  onChange={handleDateFromChange}
                />
                <DateInput
                  id='input-2'
                  type='date'
                  label='Day To'
                  fullWidth
                  placeholder='Select Day To'
                  value={filterState.day_to ?? ''}
                  onChange={handleDateToChange}
                />
              </>
            )}
          </>
        )}
      </Filters>
      {usageStatistic.jobs_data?.length > 0 ? (
        <>
          <div>
            <StyledTable>
              <TableHead>
                <tr>
                  <th>№</th>
                  <th>Mission ID</th>
                  <th>Pipeline</th>
                  <th>Area m²</th>
                  <th>Date</th>
                </tr>
              </TableHead>
              <tbody>
                {usageStatistic.jobs_data.map(l => (
                  <tr key={l.id}>
                    <td>{l.id}</td>
                    <td>{l.mission_id}</td>
                    <td>{l.pipeline}</td>
                    <td>{round(l.area, 0)}</td>
                    <td>{l.date}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
            {dataCount && (
              <Pagination
                currentPage={currentPage}
                count={dataCount}
                onSelectCallback={page =>
                  getUsageData(filterState.day_from, filterState.day_to, page)
                }
              />
            )}
          </div>
          {usageStatistic.jobs_data.length > 0 && (
            <Info>
              <ChartsContainer>
                <InfoContainer>
                  <AccentText> Total Scanned Area </AccentText>
                  <AccentText>
                    {round(usageStatistic.total_scanned_area, 0)} m²
                  </AccentText>
                </InfoContainer>
                {usageChartData.map(l => (
                  <InfoContainer key={l.id}>
                    <GaugeChart data={l} />
                  </InfoContainer>
                ))}
              </ChartsContainer>
            </Info>
          )}
        </>
      ) : (
        <HelpText>
          {loading ? 'Loading...' : 'No results, set valid filter to see results'}
        </HelpText>
      )}
    </Container>
  );
};
